<template>
    <li class="col-span-1 bg-white border border-gray-200 rounded-lg shadow divide-y divide-gray-200">
        <div class="w-full flex items-center justify-between p-6 space-x-6">
            <div class="flex-1 truncate">
                <h2 class="text-md tracking-widest uppercase font-bold text-gray-700">Latest Subscription</h2>
                <div class="flex items-center space-x-3" v-if="subscription">
                    <h3 class="text-gray-900 text-lg font-medium truncate">{{ subscription.items.data[0].price.product.name }}</h3>
                    <span class="flex-shrink-0 inline-block px-2 py-0.5 text-yellow-800 text-xs font-medium bg-yellow-100 rounded-full" v-if="Object.keys(subscription.pause_collection ?? {}).length > 0">Paused</span>
                    <span class="flex-shrink-0 inline-block px-2 py-0.5 text-red-800 text-xs font-medium bg-red-100 rounded-full" v-else-if="subscription.cancel_at_period_end && subscription.status !== 'canceled'">Cancelling</span>
                    <span class="flex-shrink-0 inline-block px-2 py-0.5 text-green-800 text-xs font-medium bg-green-100 rounded-full" v-else-if="subscription.status === 'active'">Active</span>
                    <span class="flex-shrink-0 inline-block px-2 py-0.5 text-gray-800 text-xs font-medium bg-gray-200 rounded-full" v-else-if="subscription.status === 'incomplete'">Incomplete</span>
                    <span class="flex-shrink-0 inline-block px-2 py-0.5 text-red-800 text-xs font-medium bg-red-100 rounded-full" v-else-if="subscription.status === 'canceled'">Cancelled</span>
                </div>
                <p class="mt-1 text-gray-500 text-sm truncate" v-if="subscription">
                    <template v-if="subscription.status === 'active' && !subscription.cancel_at && !subscription.cancel_at_period_end">Renews on: {{ formatDateShort(subscription.current_period_end * 1000) }}</template>
                    <template v-if="subscription.status === 'active' && subscription.cancel_at_period_end">Ends on: {{ formatDateShort(subscription.current_period_end * 1000) }}</template>
                    <template v-if="subscription.status === 'canceled'">Ended on: {{ formatDateShort(subscription.canceled_at * 1000) }}</template>
                </p>
            </div>
        </div>
        <div>
            <div class="-mt-px flex divide-x divide-gray-200">
                <div class="w-0 flex-1 flex">
                    <a href="#" @click.prevent="showEnablements" class="relative -mr-px w-0 flex-1 inline-flex items-center justify-center py-4 text-sm text-gray-700 font-medium border border-transparent rounded-bl-lg hover:text-gray-500">
                        <MagnifyingGlassCircleIcon class="w-5 h-5 text-gray-400" aria-hidden="true" />
                        <span class="ml-3">Enablements</span>
                    </a>
                </div>
                <div class="-ml-px w-0 flex-1 flex">
                    <router-link :to="{name: 'sales.new', params: { userId: user.id }}" class="relative -mr-px w-0 flex-1 inline-flex items-center justify-center py-4 text-sm text-gray-700 font-medium border border-transparent rounded-bl-lg hover:text-gray-500">
                        <CurrencyPoundIcon class="w-5 h-5 text-gray-400" aria-hidden="true" />
                        <span class="ml-3">New Sale</span>
                    </router-link>
                </div>
                <div class="-ml-px w-0 flex-1 flex relative">
                    <Menu as="template">
                        <MenuButton class="relative w-full flex-1 inline-flex items-center justify-center py-4 text-sm text-gray-700 font-medium border border-transparent rounded-br-lg hover:text-gray-500">
                            <span class="sr-only">Open user menu</span>
                            <EllipsisHorizontalCircleIcon class="w-5 h-5 text-gray-400" aria-hidden="true" />
                            <span class="ml-3">More</span>
                        </MenuButton>

                        <transition enter-active-class="transition ease-out duration-100" enter-from-class="transform opacity-0 scale-95" enter-to-class="transform opacity-100 scale-100" leave-active-class="transition ease-in duration-75" leave-from-class="transform opacity-100 scale-100" leave-to-class="transform opacity-0 scale-95">
                            <MenuItems class="origin-top-right absolute top-10 right-0 mt-2 w-48 rounded-md shadow-lg py-1 bg-white ring-1 ring-black ring-opacity-5 focus:outline-none">
                                <MenuItem v-slot="{ active }">
                                    <a :href="'https://dashboard.stripe.com/subscriptions/' + subscription.id" target="_blank" :class="[active ? 'bg-gray-100' : '', 'block px-4 py-2 text-sm text-gray-700']">View in Stripe</a>
                                </MenuItem>
                                <MenuItem v-slot="{ active }" v-if="!subscription.pause_collection">
                                    <a href="#" :class="[active ? 'bg-gray-100' : '', 'block px-4 py-2 text-sm text-gray-700']" @click.prevent="pauseSubscription">Pause Subscription</a>
                                </MenuItem>
                                <MenuItem v-slot="{ active }" v-if="Object.keys(subscription.pause_collection ?? {}).length > 0">
                                    <a href="#" :class="[active ? 'bg-gray-100' : '', 'block px-4 py-2 text-sm text-gray-700']" @click.prevent="unpauseSubscription">Unpause Subscription</a>
                                </MenuItem>
                                <MenuItem v-slot="{ active }" v-if="!subscription.cancel_at_period_end">
                                    <a href="#" :class="[active ? 'bg-gray-100' : '', 'block px-4 py-2 text-sm text-gray-700']" @click.prevent="cancelSubscription">Cancel Subscription</a>
                                </MenuItem>
                                <MenuItem v-slot="{ active }" v-if="subscription.cancel_at_period_end">
                                    <a href="#" :class="[active ? 'bg-gray-100' : '', 'block px-4 py-2 text-sm text-gray-700']" @click.prevent="uncancelSubscription">Uncancel Subscription</a>
                                </MenuItem>
                            </MenuItems>
                        </transition>
                    </Menu>
                </div>
            </div>
        </div>
    </li>
</template>

<script>
import {inject, toRef} from "vue"
import {CurrencyPoundIcon, EllipsisHorizontalCircleIcon, MagnifyingGlassCircleIcon} from "@heroicons/vue/24/outline"
import {Menu, MenuItems, MenuItem, MenuButton} from "@headlessui/vue";
import {useBus} from "../../../../app";

export default {
    name: "StripeSubscriptionBlock",
    components: {
        EllipsisHorizontalCircleIcon,
        MagnifyingGlassCircleIcon,
        CurrencyPoundIcon,
        Menu,
        MenuItems,
        MenuItem,
        MenuButton,
    },
    props: {
        subscription: {
            type: Object,
            required: true,
        },
        user: {
            type: Object,
            required: true,
        }
    },
    setup(props, context) {
        const alert = inject('alert')
        const bus = useBus()
        const subscription = toRef(props, 'subscription')
        const user = toRef(props, 'user')

        const showEnablements = () => {
            context.emit('viewEnablements', subscription);
        }

        const pauseSubscription = () => {
            alert.fire({
                title: 'Pause Subscription?',
                text: 'This will pause collection of subscription, are you sure?',
                icon: 'question',
                showCancelButton: true,
                confirmButtonText: 'Yes, pause the subscription',
                cancelButtonText: 'No, go back'
            })
                .then(async (selected) => {
                    if (selected.isConfirmed) {
                        await axios.post(route('transactions.subscriptions.pause', {subscriptionId: subscription.value.id }))
                            .then((response) => {
                                bus.$emit('alert.success', 'Subscription Paused')
                                context.emit('reload', true);
                            }).catch((e) => {
                                bus.$emit('alert.danger', 'Subscription Pause Failed')
                            })
                    }
                })
        }

        const unpauseSubscription = () => {
            alert.fire({
                title: 'Unpause Subscription?',
                text: 'This will resume collection of subscription fees, are you sure?',
                icon: 'question',
                showCancelButton: true,
                confirmButtonText: 'Yes, resume the subscription',
                cancelButtonText: 'No, go back'
            })
                .then(async (selected) => {
                    if (selected.isConfirmed) {
                        await axios.post(route('transactions.subscriptions.unpause', {subscriptionId: subscription.value.id }))
                            .then((response) => {
                                bus.$emit('alert.success', 'Subscription Unpaused')
                                context.emit('reload', true);
                            }).catch((e) => {
                                bus.$emit('alert.danger', 'Subscription Resume Failed')
                            })
                    }
                })
        }

        const cancelSubscription = () => {
            alert.fire({
                title: 'Cancel Subscription?',
                text: 'This will mark the subscription to cancel at the end of the billing period, are you sure?',
                icon: 'question',
                showCancelButton: true,
                confirmButtonText: 'Yes, cancel the subscription',
                cancelButtonText: 'No, go back'
            })
                .then(async (selected) => {
                    if (selected.isConfirmed) {
                        await axios.post(route('transactions.subscriptions.cancel', {subscriptionId: subscription.value.id }))
                            .then((response) => {
                                bus.$emit('alert.success', 'Subscription Cancelled')
                                context.emit('reload', true);
                            }).catch((e) => {
                                bus.$emit('alert.danger', 'Subscription Cancellation Failed')
                            })
                    }
                })
        }

        const uncancelSubscription = () => {
            alert.fire({
                title: 'Restore Subscription?',
                text: 'This will reactivate the subscription, are you sure?',
                icon: 'question',
                showCancelButton: true,
                confirmButtonText: 'Yes, reactivate the subscription',
                cancelButtonText: 'No, go back'
            })
                .then(async (selected) => {
                    if (selected.isConfirmed) {
                        await axios.post(route('transactions.subscriptions.uncancel', {subscriptionId: subscription.value.id }))
                            .then((response) => {
                                bus.$emit('alert.success', 'Subscription Restored')
                                context.emit('reload', true);
                            }).catch((e) => {
                                bus.$emit('alert.danger', 'Subscription Restoration Failed')
                            })
                    }
                })
        }

        return {
            user,
            subscription,
            showEnablements,
            pauseSubscription,
            unpauseSubscription,
            cancelSubscription,
            uncancelSubscription,
        }
    }
}
</script>

<style scoped>

</style>
